import React, { useState, useEffect, useRef } from 'react';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { Input, Gauge, Typeahead, Button } from '../../../components';
import {
  calculateAlternateProductCurrentSupplierGp,
  calculateAlternateProductSaving,
  calculateAlternateProductTotals,
  calculateGpIfJaybelPriceGiven,
  calculateExclusiveJaybelPriceIfGpGiven,
  calculateAlternateProductJaybelExcGSTPrice,
  calculateAlternateProductJaybelIncGSTPrice,
  calculateMainProductJaybelIncGstPrice,
} from '../../../utils/calculatePrices';
import { useAlternateProduct } from '../../../context/alternateProductContext/AlternateProductContextProvider';

const AddAlternateProduct = ({
  setAlternativeValueToNo,
  openAlternateModal,
}) => {
  const {
    productForEdit,
    setProductForEdit,
    alternateGpError,
    setAlternateGpError,
    alternateJaybelPriceError,
    setAlternateJaybelPriceError,
    alternateJaybelIncGstPriceError,
    alternateSupplierPriceError,
    setAlternateSupplierPriceError,
    alternateSupplierIncGstPriceError,
    altQuantityError,
    setAltQuantityError,
    showAlternateProductSection, // Add this line
  } = useProposal();
  const { getAlternateProductById, alternateProducts } = useAlternateProduct();
  const [showPriceOptions, setShowPriceOptions] = useState(false);
  const [showIncPriceOptions, setShowIncPriceOptions] = useState(false);
  const alternateTypeaheadRef = useRef(null);
  const [showUnitDiviserInput, setShowUnitDiviserInput] = useState(false);
  const [showUnitDiviserError, setShowUnitDiviserError] = useState('');

  // Prevent double API call for the same id and section
  const lastFetchedIdRef = useRef(null);
  useEffect(() => {
    const currentId = productForEdit.mainProduct.id;
    if (
      showAlternateProductSection &&
      currentId &&
      lastFetchedIdRef.current !== currentId
    ) {
      getAlternateProductById(currentId, alternateNo);
      lastFetchedIdRef.current = currentId;
    }
  }, [productForEdit?.mainProduct?.id, showAlternateProductSection]);

  const alternateNo = () => {
    setAlternativeValueToNo();
  };

  useEffect(() => {
    if (alternateProducts.length > 0) {
      setProductForEdit((prev) => ({
        ...prev,
        alternateProductCurrentSupplierExcGstPrice:
          productForEdit.mainProductCurrentSupplierExcGstPrice,
      }));
    }
  }, [alternateProducts, productForEdit.mainProductCurrentSupplierExcGstPrice]);

  useEffect(() => {
    if (productForEdit.alternateProductCurrentSupplierExcGstPrice) {
      updateCalculations(productForEdit);
    }
  }, [productForEdit.alternateProductCurrentSupplierExcGstPrice]);

  useEffect(() => {
    updateCalculations(productForEdit);
  }, [
    productForEdit.alternateProductCurrentSupplierExcGstPrice,
    productForEdit.alternateProductJaybelExcGstPrice,
    productForEdit.alternateProductGpRequired,
    productForEdit.alternateProductQuantity,
    productForEdit.alternateProductUnitDiviser,
    productForEdit.alternateProduct,
  ]);

  const handleKeyPress = (event) => {
    const charCode = event.charCode;
    if (charCode === 48 || charCode === 57) {
      event.preventDefault();
    }
  };

  const validateAlternateError = () => {
    let isValid = true;
    if (alternativeValue === 'Yes') {
      if (!productForEdit.alternateProductGpRequired) {
        setAlternateGpError('Alternate GP is required');
        isValid = false;
      } else {
        setAlternateGpError('');
      }

      if (!productForEdit.alternateProductCurrentSupplierExcGstPrice) {
        setAlternateSupplierPriceError(
          'Alternate Current Supplier Price is required',
        );
        isValid = false;
      } else {
        setAlternateSupplierPriceError('');
      }
    }
    return isValid;
  };

  const productOption = alternateProducts?.map((product) => ({
    label:
      product?.alternateProduct?.itemCode +
      ' - ' +
      product?.alternateProduct?.itemDescription +
      ' - ' +
      ' $ ' +
      product?.alternateProduct?.avgCost +
      (product?.alternateProduct?.inPronto === null || product?.alternateProduct?.inPronto === undefined
        ? ''
        : product?.alternateProduct?.inPronto
          ? ' <span style="float: right; color: green; font-weight: bold;">IN PRONTO</span>'
          : ' <span style="float: right; color: red; font-weight: bold;">NOT IN PRONTO</span>'),
    value: product?.alternateProduct?.id,
    data: product?.alternateProduct,
  }));

  const handleProductSelect = (selected) => {
    if (selected.length > 0) {
      const selectedProduct = selected[0].data;
      setProductForEdit((prev) => ({
        ...prev,
        alternateProduct: selectedProduct,
      }));
      if (alternateTypeaheadRef.current) {
        alternateTypeaheadRef.current.clear();
      }
    }
  };

  // Reusable function to handle alternate product price selection and calculation
  const handleAlternateProductPriceSelection = (
    optionValue,
    productForEdit,
    setProductForEdit,
  ) => {
    const excBase =
      productForEdit?.alternateProduct[`price${optionValue}exGst`];
    if (excBase !== undefined && excBase !== null && excBase !== '') {
      // Treat option value as INC
      const inc = Number(excBase);
      const exc = (inc / 1.1).toFixed(2);
      const gp = calculateGpIfJaybelPriceGiven(
        Number(exc),
        Number(productForEdit.alternateProduct.avgCost),
      );
      setProductForEdit((prev) => ({
        ...prev,
        alternateProductJaybelIncGstPrice: inc,
        alternateProductJaybelExcGstPrice: exc,
        alternateProductGpRequired: isFinite(gp) ? gp : '',
      }));
    }
  };

  const updateCalculations = (updatedProductForEdit) => {
    // GST EXEMPT RULE: If taxCode === 'E' then Exc = Inc for all prices
    if (updatedProductForEdit?.alternateProduct?.taxCode === 'E') {
      // Supplier price (ternary)
      updatedProductForEdit.alternateProductCurrentSupplierIncGstPrice =
        updatedProductForEdit.alternateProductCurrentSupplierExcGstPrice !== ''
          ? updatedProductForEdit.alternateProductCurrentSupplierExcGstPrice
          : updatedProductForEdit.alternateProductCurrentSupplierIncGstPrice;

      updatedProductForEdit.alternateProductCurrentSupplierExcGstPrice =
        updatedProductForEdit.alternateProductCurrentSupplierIncGstPrice !== ''
          ? updatedProductForEdit.alternateProductCurrentSupplierIncGstPrice
          : updatedProductForEdit.alternateProductCurrentSupplierExcGstPrice;

      // Jaybel price (ternary)
      updatedProductForEdit.alternateProductJaybelIncGstPrice =
        updatedProductForEdit.alternateProductJaybelExcGstPrice !== ''
          ? updatedProductForEdit.alternateProductJaybelExcGstPrice
          : updatedProductForEdit.alternateProductJaybelIncGstPrice;

      updatedProductForEdit.alternateProductJaybelExcGstPrice =
        updatedProductForEdit.alternateProductJaybelIncGstPrice !== ''
          ? updatedProductForEdit.alternateProductJaybelIncGstPrice
          : updatedProductForEdit.alternateProductJaybelExcGstPrice;
    }
    const quantity = updatedProductForEdit.alternateProductQuantity || 0;
    const unitDiviser = updatedProductForEdit.alternateProductUnitDiviser || 1;

    // --- Exclusive Base Price Calculations ---
    const alternateProductCurrentSupplierExcGstPrice =
      updatedProductForEdit.mainProductCurrentSupplierExcGstPrice * unitDiviser;

    const alternateProductCurrentSupplierGp =
      calculateAlternateProductCurrentSupplierGp(
        alternateProductCurrentSupplierExcGstPrice,
        updatedProductForEdit.alternateProduct?.avgCost,
      );

    const alternateProductSaving = calculateAlternateProductSaving(
      alternateProductCurrentSupplierExcGstPrice,
      updatedProductForEdit.alternateProductJaybelExcGstPrice,
    );

    const {
      alternateProductJaybelExcGstPriceTotal,
      alternateProductCurrentSupplierExcGstTotal,
      alternateProductTotal,
    } = calculateAlternateProductTotals(
      quantity,
      updatedProductForEdit.alternateProductJaybelExcGstPrice,
      alternateProductCurrentSupplierExcGstPrice,
    );

    const alternateProductQuotePrice = alternateProductTotal / (quantity || 1);

    // --- Supplier Inclusive GST (Always independent from quantity) ---
    // Supplier Inclusive GST (ternary)
    let alternateProductCurrentSupplierIncGstPrice =
      updatedProductForEdit?.alternateProduct?.taxCode !== 'E'
        ? alternateProductCurrentSupplierExcGstPrice
          ? Number(
              (alternateProductCurrentSupplierExcGstPrice * 1.1).toFixed(2),
            )
          : ''
        : alternateProductCurrentSupplierExcGstPrice;

    // Supplier INC GST Total (ternary)
    let alternateProductCurrentSupplierIncGstPriceTotal =
      alternateProductCurrentSupplierIncGstPrice && quantity > 0
        ? Number(
            (alternateProductCurrentSupplierIncGstPrice * quantity).toFixed(2),
          )
        : alternateProductCurrentSupplierExcGstTotal
          ? Number(
              (alternateProductCurrentSupplierExcGstTotal * 1.1).toFixed(2),
            )
          : null;

    // Jaybel INC GST Base Price (ternary)
    let alternateProductJaybelIncGstPrice =
      productForEdit?.alternateProduct?.taxCode === 'E'
        ? updatedProductForEdit.alternateProductJaybelExcGstPrice
        : (updatedProductForEdit.alternateProductJaybelIncGstPrice === '' ||
              updatedProductForEdit.alternateProductJaybelIncGstPrice ===
                null) &&
            updatedProductForEdit.alternateProductJaybelExcGstPrice
          ? Number(
              (
                updatedProductForEdit.alternateProductJaybelExcGstPrice * 1.1
              ).toFixed(2),
            )
          : (updatedProductForEdit.alternateProductJaybelIncGstPrice ?? null);

    // Jaybel INC GST Total (ternary)
    let alternateProductJaybelIncGstPriceTotal =
      productForEdit?.alternateProduct?.taxCode === 'E'
        ? alternateProductJaybelExcGstPriceTotal
        : alternateProductJaybelIncGstPrice && quantity > 0
          ? Number((alternateProductJaybelIncGstPrice * quantity).toFixed(2))
          : alternateProductJaybelExcGstPriceTotal
            ? Number((alternateProductJaybelExcGstPriceTotal * 1.1).toFixed(2))
            : null;

    // --- Final State Update ---
    setProductForEdit({
      ...updatedProductForEdit,

      alternateProductCurrentSupplierExcGstPrice,
      alternateProductCurrentSupplierGp: isFinite(
        alternateProductCurrentSupplierGp,
      )
        ? alternateProductCurrentSupplierGp
        : '',

      alternateProductSaving: isFinite(alternateProductSaving)
        ? alternateProductSaving
        : '',

      alternateProductJaybelExcGstPriceTotal: isFinite(
        alternateProductJaybelExcGstPriceTotal,
      )
        ? alternateProductJaybelExcGstPriceTotal
        : '',

      alternateProductCurrentSupplierExcGstTotal: isFinite(
        alternateProductCurrentSupplierExcGstTotal,
      )
        ? alternateProductCurrentSupplierExcGstTotal
        : '',

      alternateProductTotal: isFinite(alternateProductTotal)
        ? alternateProductTotal
        : '',

      alternateProductQuotePrice: isFinite(alternateProductQuotePrice)
        ? alternateProductQuotePrice
        : '',

      // --- NEW Inclusive Fields (Fixed) ---
      alternateProductCurrentSupplierIncGstPrice:
        alternateProductCurrentSupplierIncGstPrice ?? '',

      alternateProductJaybelIncGstPrice:
        alternateProductJaybelIncGstPrice ?? '',

      alternateProductCurrentSupplierIncGstPriceTotal: isFinite(
        alternateProductCurrentSupplierIncGstPriceTotal,
      )
        ? alternateProductCurrentSupplierIncGstPriceTotal
        : '',

      alternateProductJaybelIncGstPriceTotal: isFinite(
        alternateProductJaybelIncGstPriceTotal,
      )
        ? alternateProductJaybelIncGstPriceTotal
        : '',
    });
  };

  const handleGpRequiredChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductGpRequired: '',
        alternateProductJaybelExcGstPrice: '',
        alternateProductJaybelIncGstPrice: '',
      };
      updateCalculations(updatedProductForEdit);
      setAlternateGpError('');
    } else {
      const alternateProductGpRequired = parseFloat(value);
      if (!isNaN(alternateProductGpRequired)) {
        if (alternateProductGpRequired > 100) {
          setAlternateGpError('GP Required cannot be more than 100%');
        } else {
          // Calculate Jaybel Exc GST Price using required GP (%) and current customer exclusive price
          const currentCustomerExcPrice = Number(
            productForEdit.alternateProductCurrentSupplierExcGstPrice,
          );
          const alternateProductJaybelExcGstPrice =
            calculateExclusiveJaybelPriceIfGpGiven(
              productForEdit.alternateProduct.avgCost,
              alternateProductGpRequired,
            );
          // Always calculate inclusive price if exclusive is calculated
          const alternateProductJaybelIncGstPrice = isFinite(
            alternateProductJaybelExcGstPrice,
          )
            ? calculateMainProductJaybelIncGstPrice(
                alternateProductJaybelExcGstPrice,
              )
            : '';
          const updatedProductForEdit = {
            ...productForEdit,
            alternateProductGpRequired: isFinite(alternateProductGpRequired)
              ? alternateProductGpRequired
              : '',
            alternateProductJaybelExcGstPrice: isFinite(
              alternateProductJaybelExcGstPrice,
            )
              ? alternateProductJaybelExcGstPrice
              : '',
            alternateProductJaybelIncGstPrice,
          };
          updateCalculations(updatedProductForEdit);
          setAlternateGpError('');
        }
      }
    }
  };

  const handleJaybelPriceChange = (event) => {
    const value = event.target.value;
    if (value === '' || !isNaN(Number(value))) {
      let exc = value === '' ? '' : Number(value);
      let inc = value;

      if (productForEdit?.alternateProduct?.taxCode !== 'E') {
        inc =
          value === '' ? '' : calculateAlternateProductJaybelIncGSTPrice(exc);
      }
      exc = value === '' ? '' : exc.toString();
      inc = value === '' ? '' : inc.toString();
      const alternateProductGpRequired =
        value === ''
          ? ''
          : calculateGpIfJaybelPriceGiven(
              Number(exc),
              Number(productForEdit.alternateProduct.avgCost),
            );
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductJaybelExcGstPrice: exc,
        alternateProductJaybelIncGstPrice: inc,
        alternateProductGpRequired: isFinite(alternateProductGpRequired)
          ? alternateProductGpRequired
          : '',
      };
      if (alternateProductGpRequired > 100) {
        setAlternateGpError('GP Required cannot be more than 100%');
      } else {
        setAlternateGpError('');
      }
      setAlternateJaybelPriceError('');
      setProductForEdit(updatedProductForEdit);
      updateCalculations(updatedProductForEdit);
    }
  };

  // const handleCurrentSupplierPriceChange = (event) => {
  //   const value = event.target.value;
  //   const alternateProductCurrentSupplierExcGstPrice = parseFloat(value);
  //   if (value === '' || !isNaN(alternateProductCurrentSupplierExcGstPrice)) {
  //     const updatedProductForEdit = {
  //       ...productForEdit,
  //       alternateProductCurrentSupplierExcGstPrice: value,
  //     };
  //     // if (
  //     //   alternateProductCurrentSupplierExcGstPrice <
  //     //   productForEdit.alternateProduct.avgCost
  //     // ) {
  //     //   setAlternateSupplierPriceError(
  //     //     'Supplier Price cannot be less than the cost',
  //     //   );
  //     // } else {
  //     setAlternateSupplierPriceError('');
  //     //}
  //     updateCalculations(updatedProductForEdit);
  //   }
  // };

  const handleQuantityChange = (event) => {
    const value = event.target.value;
    const alternateProductQuantity = value === '' ? '' : parseInt(value, 10);
    if (value === '' || !isNaN(alternateProductQuantity)) {
      if (alternateProductQuantity < 0) {
        setAltQuantityError('Quantity must be zero or greater');
      } else {
        setAltQuantityError('');
      }
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductQuantity: isFinite(alternateProductQuantity)
          ? alternateProductQuantity
          : '',
      };
      setProductForEdit(updatedProductForEdit); // Ensure state is updated
      updateCalculations(updatedProductForEdit);
    }
  };

  const handlePriceOptionChange = (event) => {
    const selectedOption = event.target.value;
    const priceKey = `price${selectedOption}exGst`;
    const selectedPrice = productForEdit.alternateProduct?.[priceKey];
    if (selectedPrice !== undefined) {
      const alternateProductJaybelExcGstPrice = parseFloat(selectedPrice);
      const alternateProductJaybelIncGstPrice = isFinite(
        alternateProductJaybelExcGstPrice,
      )
        ? (alternateProductJaybelExcGstPrice * 1.1).toFixed(2)
        : '';
      const alternateProductGpRequired = calculateGpIfJaybelPriceGiven(
        alternateProductJaybelExcGstPrice,
        Number(productForEdit.alternateProduct.avgCost),
      );
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductJaybelExcGstPrice: isFinite(
          alternateProductJaybelExcGstPrice,
        )
          ? alternateProductJaybelExcGstPrice
          : '',
        alternateProductJaybelIncGstPrice: alternateProductJaybelIncGstPrice,
        alternateProductGpRequired: isFinite(alternateProductGpRequired)
          ? alternateProductGpRequired
          : '',
      };
      updateCalculations(updatedProductForEdit);
      setProductForEdit(updatedProductForEdit);
    }
  };

  // Handler for Jaybel Price Inc (alternate)
  const handleAlternateJaybelIncGstPriceChange = (e) => {
    const value = e.target.value;
    if (value === '' || !isNaN(Number(value))) {
      let inc = value === '' ? '' : Number(value);
      let exc = value;

      if (productForEdit?.alternateProduct?.taxCode !== 'E') {
        exc =
          value === '' ? '' : calculateAlternateProductJaybelExcGSTPrice(inc);
      }
      inc = value === '' ? '' : inc.toString();
      exc = value === '' ? '' : exc.toString();
      // Recalculate GP
      const alternateProductGpRequired =
        value === ''
          ? ''
          : calculateGpIfJaybelPriceGiven(
              Number(exc),
              Number(productForEdit.alternateProduct.avgCost),
            );
      setProductForEdit((prev) => ({
        ...prev,
        alternateProductJaybelIncGstPrice: inc,
        alternateProductJaybelExcGstPrice: exc,
        alternateProductGpRequired: isFinite(alternateProductGpRequired)
          ? alternateProductGpRequired
          : '',
      }));
      if (alternateProductGpRequired > 100) {
        setAlternateGpError('GP Required cannot be more than 100%');
      } else {
        setAlternateGpError('');
      }
    }
  };

  const handleUnitDiviserCheckboxChange = (event) => {
    setShowUnitDiviserInput(event.target.checked);
    if (!event.target.checked) {
      setShowUnitDiviserError('');
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductUnitDiviser: 1,
      };
      setProductForEdit(updatedProductForEdit);
      updateCalculations(updatedProductForEdit);
    }
  };

  const handleUnitDiviserChange = (event) => {
    const value = parseFloat(event.target.value);
    if (value <= 0) {
      setShowUnitDiviserError('Unit Diviser must be greater than zero');
    } else {
      setShowUnitDiviserError('');
      const updatedProductForEdit = {
        ...productForEdit,
        alternateProductUnitDiviser: value,
        alternateProductCurrentSupplierExcGstPrice:
          productForEdit.alternateProductCurrentSupplierExcGstPrice * value,
      };
      setProductForEdit(updatedProductForEdit);
      updateCalculations(updatedProductForEdit);
    }
  };

  const handleAddAlternateProduct = () => {
    const mainProduct = productForEdit?.mainProduct || {};

    openAlternateModal({ mainProduct, alternateProducts }); // Pass main product and alternate products
  };

  return (
    <div className=" p-5 shadow-lg rounded-xl bg-white z-0">
      <h2 className="text-xl font-bold mb-4">
        Alternate Product Information :{' '}
        <span className="font-mono text-slate-700">
          {productForEdit?.alternateProduct?.itemCode}{' '}
          <span className="text-sm">
            ( {productForEdit?.alternateProduct?.itemDescription} )
          </span>{' '}
        </span>{' '}
      </h2>
      <div className="flex items-center gap-4">
        <div className="w-7/12">
          <Typeahead
            ref={alternateTypeaheadRef}
            label="Alternate Product"
            options={productOption}
            labelKey="label"
            customHeight={true}
            disabledCreateNew={true}
            disabled={!!productForEdit?.alternateProduct}
            onChange={handleProductSelect}
            placeholder="Choose an alternate product"
          />
        </div>
        <div className="w-3/12">
          <Button onClick={handleAddAlternateProduct}>
            Add Alternate Product
          </Button>
        </div>
        <div className=" text-center">
          {' '}
          <div className="">
            <Gauge value={productForEdit.alternateProductSaving || 0} />
          </div>
          <div>Product Saving</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Input
          required
          label="Cost"
          value={productForEdit?.alternateProduct?.avgCost}
          disabled
        />
        <Input
          label="Current Customer Price Exc"
          value={productForEdit.alternateProductCurrentSupplierExcGstPrice}
          disabled
          type="number"
          required
          step="0.01"
          error={alternateSupplierPriceError}
        />

        <Input
          label={
            <span className="text-red-400 font-semibold">
              Current Customer Price Inc
            </span>
          }
          value={productForEdit.alternateProductCurrentSupplierIncGstPrice}
          disabled
          type="number"
          required
          step="0.01"
          error={alternateSupplierIncGstPriceError}
        />
        <Input
          label="GP Required (%)"
          type="text"
          onKeyPress={handleKeyPress}
          inputMode="numeric"
          required
          step="0.01"
          value={productForEdit?.alternateProductGpRequired}
          onChange={handleGpRequiredChange}
          error={alternateGpError}
          disabled={productForEdit?.alternateProduct == null}
        />
        <div className="relative">
          <Input
            label="Jaybel Price Exc"
            type="number"
            step="0.01"
            required
            value={productForEdit?.alternateProductJaybelExcGstPrice}
            onChange={handleJaybelPriceChange}
            onFocus={() => setShowPriceOptions(true)}
            onBlur={() => setTimeout(() => setShowPriceOptions(false), 200)}
            error={alternateJaybelPriceError}
            disabled={productForEdit?.alternateProduct == null}
          />
          {showPriceOptions && (
            <div
              className="absolute z-10 bg-white border border-gray-300 rounded shadow-lg mt-1"
              onMouseDown={(e) => e.preventDefault()}>
              <ul className="list-none p-2">
                {[
                  { value: '0', label: 'Price 0' },
                  { value: '1', label: 'Price 1' },
                  { value: '2', label: 'Price 2' },
                  { value: '3', label: 'Price 3' },
                  { value: '4', label: 'Price 4' },
                ].map((option) => (
                  <li
                    key={option.value}
                    className="cursor-pointer p-1 hover:bg-gray-200 px-5"
                    onClick={() => {
                      handlePriceOptionChange({
                        target: { value: option.value },
                      });
                      setShowPriceOptions(false);
                    }}>
                    {option.label} -{' '}
                    {isFinite(
                      productForEdit?.alternateProduct[
                        `price${option.value}exGst`
                      ],
                    )
                      ? productForEdit?.alternateProduct[
                          `price${option.value}exGst`
                        ]
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="relative">
          <Input
            label={
              <span className="text-red-400 font-semibold">
                Jaybel Price Inc
              </span>
            }
            type="number"
            step="0.01"
            required
            value={productForEdit?.alternateProductJaybelIncGstPrice || ''}
            onChange={handleAlternateJaybelIncGstPriceChange}
            onFocus={() => setShowIncPriceOptions(true)}
            onBlur={() => setTimeout(() => setShowIncPriceOptions(false), 200)}
            error={alternateJaybelIncGstPriceError}
            disabled={productForEdit?.alternateProduct == null}
          />
          {showIncPriceOptions && (
            <div
              className="absolute z-10 bg-white border border-gray-300 rounded shadow-lg mt-1"
              onMouseDown={(e) => e.preventDefault()}>
              <ul className="list-none p-2">
                {[
                  { value: '0', label: 'Price 0' },
                  { value: '1', label: 'Price 1' },
                  { value: '2', label: 'Price 2' },
                  { value: '3', label: 'Price 3' },
                  { value: '4', label: 'Price 4' },
                ].map((option) => (
                  <li
                    key={option.value}
                    className="cursor-pointer p-1 hover:bg-gray-200 px-5"
                    onClick={() => {
                      // Use exGst price, convert to incGst, and calculate GP
                      handleAlternateProductPriceSelection(
                        option.value,
                        productForEdit,
                        setProductForEdit,
                      );
                    }}>
                    {option.label} -{' '}
                    {isFinite(
                      productForEdit?.alternateProduct[
                        `price${option.value}exGst`
                      ],
                    )
                      ? (productForEdit?.alternateProduct[
                          `price${option.value}exGst`
                        ]).toFixed(2)
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Input
          label="Supplier Gp(%)"
          step="0.01"
          value={productForEdit.alternateProductCurrentSupplierGp}
          disabled
          required
        />

        <Input
          label="Quantity"
          type="number"
          required
          value={productForEdit.alternateProductQuantity || ''}
          onChange={handleQuantityChange}
          error={altQuantityError}
        />
        <div className="">
          <label htmlFor="unitDiviserCheckbox">Alt Unit Diviser</label>
          <div className="flex h-10 items-center m-2">
            <input
              type="checkbox"
              id="unitDiviserCheckbox"
              checked={
                showUnitDiviserInput ||
                productForEdit?.alternateProductUnitDiviser > 1
              }
              onChange={handleUnitDiviserCheckboxChange}
            />

            {(showUnitDiviserInput ||
              productForEdit?.alternateProductUnitDiviser > 1) && (
              <Input
                // label="Unit Diviser"
                type="number"
                min="1"
                required
                value={productForEdit?.alternateProductUnitDiviser}
                onChange={handleUnitDiviserChange}
                error={showUnitDiviserError}
              />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Product Description</h3>
          <div>
            <div className="mb-1">Description :</div>
            <div>
              {productForEdit?.alternateProduct?.itemDescription} {' - '}{' '}
              {productForEdit?.alternateProduct?.description2}
            </div>
          </div>
        </div>

        <div className="bg-gray-100 p-4 rounded-lg text-sm">
          <h3 className="font-semibold mb-2">Pricing & Quantity</h3>
          <div className="space-y-1">
            <div>
              Price 0:{' '}
              {isFinite(productForEdit?.alternateProduct?.price0exGst)
                ? productForEdit?.alternateProduct?.price0exGst
                : ''}{' '}
              <br /> Qty 0:{' '}
              {isFinite(productForEdit?.alternateProduct?.qtyBreak0)
                ? productForEdit?.alternateProduct?.qtyBreak0
                : ''}
            </div>
            <div>
              Price 1:{' '}
              {isFinite(productForEdit?.alternateProduct?.price1exGst)
                ? productForEdit?.alternateProduct?.price1exGst
                : ''}{' '}
              <br /> Qty 1:{' '}
              {isFinite(productForEdit?.alternateProduct?.qtyBreak1)
                ? productForEdit?.alternateProduct?.qtyBreak1
                : ''}
            </div>
            <div>
              Price 2:{' '}
              {isFinite(productForEdit?.alternateProduct?.price2exGst)
                ? productForEdit?.alternateProduct?.price2exGst
                : ''}{' '}
              <br /> Qty 2:{' '}
              {isFinite(productForEdit?.alternateProduct?.qtyBreak2)
                ? productForEdit?.alternateProduct?.qtyBreak2
                : ''}
            </div>
            <div>
              Price 3:{' '}
              {isFinite(productForEdit?.alternateProduct?.price3exGst)
                ? productForEdit?.alternateProduct?.price3exGst
                : ''}{' '}
              <br /> Qty 3:{' '}
              {isFinite(productForEdit?.alternateProduct?.qtyBreak3)
                ? productForEdit?.alternateProduct?.qtyBreak3
                : ''}
            </div>
            <div>
              Price 4:{' '}
              {isFinite(productForEdit?.alternateProduct?.price4exGst)
                ? productForEdit?.alternateProduct?.price4exGst
                : ''}{' '}
              <br /> Qty 4:{' '}
              {isFinite(productForEdit?.alternateProduct?.qtyBreak4)
                ? productForEdit?.alternateProduct?.qtyBreak4
                : ''}
            </div>
          </div>
        </div>

        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Stock Information </h3>
          <div className="space-y-1">
            <div>
              Managed Range: {productForEdit?.alternateProduct?.managedRange}
            </div>
            <div>
              FLC Page No: {productForEdit?.alternateProduct?.flcPageNo}
            </div>
            <div>
              Available Online:{' '}
              {productForEdit?.alternateProduct?.internetFlag ? 'Yes' : 'No'}
            </div>
          </div>
        </div>

        <div className="bg-gray-100 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Warehouse Information</h3>
          <div className="space-y-1">
            <div>
              WHSE Max:{' '}
              {isFinite(productForEdit?.alternateProduct?.maximumStock)
                ? productForEdit?.alternateProduct?.maximumStock
                : ''}
            </div>
            <div>
              WHSE Min:{' '}
              {isFinite(productForEdit?.alternateProduct?.minimumStock)
                ? productForEdit?.alternateProduct?.minimumStock
                : ''}
            </div>
            <div>Bin Location: {productForEdit?.alternateProduct?.binLoc}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAlternateProduct;
